import { useEffect, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { makeStyles } from '@material-ui/core';
import { MeshField } from './MeshField';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 18,
    border: '1px solid var(--q-border)',
    background:
      'linear-gradient(120deg, var(--q-bg3) 0%, var(--q-bg2) 45%, var(--q-bg) 100%)',
    padding: '48px 40px',
    // Split, not centred — asymmetric per the variance dial.
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 32,
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
      padding: '32px 20px',
    },
  },
  copy: { position: 'relative', zIndex: 1, minWidth: 0 },
  title: {
    fontFamily: 'var(--q-font-display)',
    fontWeight: 800,
    fontSize: 'clamp(44px, 7vw, 92px)',
    lineHeight: 0.9,
    letterSpacing: '-0.03em',
    margin: '10px 0 0',
    color: 'var(--q-text)',
  },
  emerald: { color: 'var(--q-emerald)' },
  lede: {
    color: 'var(--q-sub)',
    fontSize: 16,
    lineHeight: 1.6,
    maxWidth: '52ch',
    marginTop: 18,
  },
  meta: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 26,
    alignItems: 'center',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    borderRadius: 999,
    border: '1px solid var(--q-border-strong)',
    background: 'rgba(255,255,255,0.02)',
    fontFamily: 'var(--q-font-mono)',
    fontSize: 11,
    letterSpacing: '0.08em',
    color: 'var(--q-sub)',
  },
  kbd: {
    fontFamily: 'var(--q-font-mono)',
    fontSize: 11,
    padding: '3px 7px',
    borderRadius: 5,
    border: '1px solid var(--q-border-strong)',
    background: 'var(--q-bg)',
    color: 'var(--q-text)',
  },
});

// Brand "smooth decel" curve as a fixed tuple so it types as a bezier.
const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.5, ease: EASE_OUT },
  }),
};

export const Hero = ({ userName }: { userName?: string }) => {
  const classes = useStyles();
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const hour = clock.getHours();
  const partOfDay =
    hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  return (
    <div className={classes.root}>
      <MeshField height={340} />
      <div className={classes.copy}>
        <motion.div initial="hidden" animate="show">
          <motion.div variants={fadeUp} custom={0}>
            <span className="q-eyebrow">AI Workforce Transformation</span>
          </motion.div>

          <motion.h1 variants={fadeUp} custom={1} className={classes.title}>
            Quod<span className={classes.emerald}>in</span>
          </motion.h1>

          <motion.p variants={fadeUp} custom={2} className={classes.lede}>
            {userName ? `Good ${partOfDay}, ${userName}. ` : ''}
            Every service, workflow and model your team runs — catalogued,
            documented and one keystroke away. Ship the thing, not the setup.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className={classes.meta}>
            <span className={classes.pill}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--q-emerald)',
                  boxShadow: '0 0 6px var(--q-emerald)',
                  animation: 'q-breathe 3s ease-in-out infinite',
                }}
              />
              PLATFORM LIVE
            </span>
            <span className={classes.pill}>
              SEARCH <span className={classes.kbd}>⌘</span>
              <span className={classes.kbd}>K</span>
            </span>
            <span className={classes.pill}>QUODE.TECH</span>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
